import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Save, Plus, X, ArrowUp, ArrowDown, Gauge, Boxes, Palette, Type, Search, Home as HomeIcon, Eye, EyeOff, Instagram, Facebook, Youtube, Twitter, Music2, MessageCircle, Truck, Shield, Award, CreditCard, Landmark, Copy, Trash2, Star, Zap, Package, CheckCircle, Lock, Gift, Phone, Clock, RotateCcw, HeartHandshake, ShoppingCart, Bell, MapPin, Flame } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import EmojiPicker from '../../components/EmojiPicker';
import ImageUploader from '../../components/ImageUploader';

const SPEED_PRESETS = [
  { id: 'slow', label: 'Lento', value: 60 },
  { id: 'medium', label: 'Medio', value: 40 },
  { id: 'fast', label: 'Rápido', value: 22 },
  { id: 'turbo', label: 'Turbo', value: 12 },
];

const SettingsSection = () => {
  const [s, setS] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const DEFAULT_HOME_SECTIONS = React.useMemo(() => ([
    { id: 'hero', enabled: true, title: 'Hero (carrusel)', subtitle: null },
    { id: 'categories', enabled: true, title: 'Categorías', subtitle: 'Encuentra lo que buscas' },
    { id: 'benefits', enabled: true, title: 'Beneficios', subtitle: null },
    { id: 'vhill_top', enabled: true, title: 'Los más vendidos', subtitle: 'Nuestras estrellas VHILL en 3K, 12K y 32K' },
    { id: 'featured', enabled: true, title: 'Novedades', subtitle: 'Los productos más buscados de la semana' },
    { id: 'desechables', enabled: true, title: 'Vapes Desechables', subtitle: 'Listos para vapear, máxima autonomía' },
    { id: 'ejuice', enabled: true, title: 'E-Juice', subtitle: 'Líquidos premium para tu vape' },
    { id: 'brands', enabled: true, title: 'Marcas reconocidas', subtitle: 'Solo lo más original' },
    { id: 'kits', enabled: true, title: 'Kits y Mods', subtitle: 'Para el vapeador más exigente' },
    { id: 'testimonials', enabled: true, title: 'Testimonios', subtitle: null },
    { id: 'how_to_order', enabled: true, title: '¿Cómo hago un pedido?', subtitle: 'Simple, rápido y seguro.' },
  ]), []);

  useEffect(() => {
    api.get('/admin/categories').then((r) => setAllCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/admin/settings').then((r) => {
      const data = r.data || {};
      // Backfill home_sections defaults if missing (legacy settings)
      if (!Array.isArray(data.home_sections) || data.home_sections.length === 0) {
        data.home_sections = DEFAULT_HOME_SECTIONS;
      } else {
        // Append how_to_order if missing (added in recent update)
        const ids = new Set(data.home_sections.map((s) => s.id));
        if (!ids.has('how_to_order')) {
          data.home_sections = [...data.home_sections, { id: 'how_to_order', enabled: true, title: '¿Cómo hago un pedido?', subtitle: 'Simple, rápido y seguro.' }];
        }
      }
      // Backfill home_subcategories defaults if missing
      if (!Array.isArray(data.home_subcategories) || data.home_subcategories.length === 0) {
        data.home_subcategories = [
          { id: 'vhill_3k',  label: 'VHILL 3K',  category: 'VHILL 3K',  color: '#f97316', visible: true, order: 1 },
          { id: 'vhill_12k', label: 'VHILL 12K', category: 'VHILL 12K', color: '#f97316', visible: true, order: 2 },
          { id: 'vhill_32k', label: 'VHILL 32K', category: 'VHILL 32K', color: '#f97316', visible: true, order: 3 },
        ];
      }
      setS(data);
    }).finally(() => setLoading(false));
  }, [DEFAULT_HOME_SECTIONS]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/settings', {
        ...s,
        free_shipping_threshold: Number(s.free_shipping_threshold) || 0,
        shipping_cost: Number(s.shipping_cost) || 0,
        marquee_speed: Number(s.marquee_speed) || 40,
        low_stock_threshold: Math.max(1, Number(s.low_stock_threshold) || 5),
        restock_step: Math.max(1, Number(s.restock_step) || 10),
        logo_height: Math.max(20, Math.min(96, Number(s.logo_height) || 40)),
      });
      toast({ title: 'Ajustes guardados' });
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail }); }
    finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-orange-500" />;
  if (!s) return null;

  const setMsg = (i, v) => {
    const arr = [...(s.marquee_messages || [])];
    arr[i] = v;
    setS({ ...s, marquee_messages: arr });
  };
  const addMsg = () => setS({ ...s, marquee_messages: [...(s.marquee_messages || []), ''] });
  const rmMsg = (i) => {
    const arr = [...(s.marquee_messages || [])];
    arr.splice(i, 1);
    setS({ ...s, marquee_messages: arr });
  };
  const moveMsg = (i, delta) => {
    const arr = [...(s.marquee_messages || [])];
    const j = i + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setS({ ...s, marquee_messages: arr });
  };

  // Home sections helpers
  const setSection = (i, patch) => {
    const arr = [...(s.home_sections || [])];
    arr[i] = { ...arr[i], ...patch };
    setS({ ...s, home_sections: arr });
  };
  const moveSection = (i, delta) => {
    const arr = [...(s.home_sections || [])];
    const j = i + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setS({ ...s, home_sections: arr });
  };
  const removeSection = (i) => {
    const arr = [...(s.home_sections || [])];
    arr.splice(i, 1);
    setS({ ...s, home_sections: arr });
  };
  const addSection = (secId) => {
    const arr = [...(s.home_sections || [])];
    if (secId === 'custom_category') {
      arr.push({
        id: `custom_${Date.now().toString(36)}`,
        type: 'category',
        enabled: true,
        title: 'Nueva categoría',
        subtitle: 'Descripción de la sección',
        category: '',
      });
    } else {
      const d = DEFAULT_HOME_SECTIONS.find((x) => x.id === secId);
      if (d) {
        arr.push({ ...d, enabled: true });
      }
    }
    setS({ ...s, home_sections: arr });
  };
  const setBenefitCard = (secIdx, cardIdx, patch) => {
    const arr = [...(s.home_sections || [])];
    const sec = { ...arr[secIdx] };
    const cards = [...(sec.cards || [
      { icon: 'flame', title: 'Vapes Desechables', desc: 'Sabor listo para usar', link: '/catalogo?cat=Vape%20Desechable' },
      { icon: 'zap', title: 'Kits y Mods', desc: 'Máxima potencia', link: '/catalogo?cat=Kits' },
      { icon: 'sparkles', title: 'E-Juice Premium', desc: 'Sabores originales', link: '/catalogo?cat=e-juice' }
    ])];
    cards[cardIdx] = { ...cards[cardIdx], ...patch };
    sec.cards = cards;
    arr[secIdx] = sec;
    setS({ ...s, home_sections: arr });
  };
  const SECTION_INFO = {
    hero: { label: 'Hero (carrusel de banners)', hasText: false },
    categories: { label: 'Categorías (grid)', hasText: true },
    benefits: { label: 'Beneficios (3 tarjetas)', hasText: false },
    vhill_top: { label: 'Los más vendidos (grids por sub-categoría)', hasText: true },
    featured: { label: 'Novedades (grid de productos)', hasText: true },
    desechables: { label: 'Vapes Desechables', hasText: true },
    ejuice: { label: 'E-Juice', hasText: true },
    brands: { label: 'Marcas reconocidas', hasText: true },
    kits: { label: 'Kits y Mods', hasText: true },
    testimonials: { label: 'Reseñas de clientes', hasText: false },
  };

  // Home sub-categories helpers (drives "Los más vendidos" section)
  const setSubcat = (i, patch) => {
    const arr = [...(s.home_subcategories || [])];
    arr[i] = { ...arr[i], ...patch };
    setS({ ...s, home_subcategories: arr });
  };
  const moveSubcat = (i, delta) => {
    const arr = [...(s.home_subcategories || [])];
    const j = i + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    arr.forEach((row, idx) => (row.order = idx + 1));
    setS({ ...s, home_subcategories: arr });
  };
  const addSubcat = () => {
    const arr = [...(s.home_subcategories || [])];
    const nextOrder = arr.length + 1;
    arr.push({
      id: `sub_${Date.now().toString(36)}`,
      label: 'Nueva sub-categoría',
      category: '',
      color: '#f97316',
      order: nextOrder,
      visible: true,
    });
    setS({ ...s, home_subcategories: arr });
  };
  const removeSubcat = (i) => {
    if (!window.confirm('¿Eliminar esta sub-categoría del home?')) return;
    const arr = [...(s.home_subcategories || [])];
    arr.splice(i, 1);
    arr.forEach((row, idx) => (row.order = idx + 1));
    setS({ ...s, home_subcategories: arr });
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white">Ajustes del sitio</h1>
          <p className="text-white/50 text-sm mt-1">Configura contenido global, envíos y contacto.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-black font-bold shrink-0">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
        </Button>
      </div>

      <div className="space-y-6">
        {/* Home sections */}
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <HomeIcon className="w-4 h-4 text-orange-400" />
                <h3 className="text-white font-bold">Secciones del inicio</h3>
              </div>
              <p className="text-white/50 text-xs">Reordena, oculta, edita o elimina las secciones de la página principal.</p>
            </div>
            <div className="flex gap-2 items-center shrink-0">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addSection(e.target.value);
                    e.target.value = ''; // reset
                  }
                }}
                className="h-9 rounded-md bg-neutral-900 border border-white/20 text-white text-xs px-2.5 focus:outline-none focus:border-orange-500 cursor-pointer"
                data-testid="add-section-select"
              >
                <option value="">+ Añadir sección...</option>
                <option value="custom_category">Sección de Categoría Personalizada</option>
                {DEFAULT_HOME_SECTIONS.filter(
                  (d) => !(s.home_sections || []).some((x) => x.id === d.id)
                ).map((d) => (
                  <option key={d.id} value={d.id}>
                    Re-añadir: {SECTION_INFO[d.id]?.label || d.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            {(s.home_sections || []).map((sec, i) => {
              const info = SECTION_INFO[sec.id] || { label: sec.id, hasText: true };
              const total = (s.home_sections || []).length;
              const isCustom = sec.type === 'category' || sec.id.startsWith('custom_');
              return (
                <div key={sec.id} data-testid={`home-section-${sec.id}`} className={`rounded-xl border p-3 transition ${sec.enabled === false ? 'border-white/5 bg-white/[0.02] opacity-60' : 'border-white/10 bg-black/30'}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px] font-black flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate flex items-center gap-2">
                        {isCustom && <span className="inline-block w-1.5 h-3 bg-orange-500 rounded-sm"></span>}
                        {sec.title || info.label}
                      </p>
                      <p className="text-white/40 text-[10px] uppercase tracking-wider">
                        {isCustom ? `Sección de categoría: ${sec.category || 'Sin elegir'}` : info.label}
                      </p>
                    </div>
                    <button
                      onClick={() => setSection(i, { enabled: sec.enabled === false })}
                      data-testid={`toggle-${sec.id}`}
                      title={sec.enabled === false ? 'Mostrar' : 'Ocultar'}
                      className={`w-9 h-9 rounded-md inline-flex items-center justify-center transition ${
                        sec.enabled === false
                          ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20'
                          : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                      }`}
                    >
                      {sec.enabled === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => moveSection(i, -1)}
                      disabled={i === 0}
                      className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center disabled:opacity-30"
                      title="Subir"
                    ><ArrowUp className="w-4 h-4" /></button>
                    <button
                      onClick={() => moveSection(i, 1)}
                      disabled={i === total - 1}
                      className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center disabled:opacity-30"
                      title="Bajar"
                    ><ArrowDown className="w-4 h-4" /></button>
                    <button
                      onClick={() => removeSection(i)}
                      data-testid={`delete-${sec.id}`}
                      className="w-9 h-9 rounded-md bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 inline-flex items-center justify-center transition"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {sec.enabled !== false && (
                    <div className={`mt-3 grid grid-cols-1 gap-2 pl-8 ${isCustom ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Título</label>
                        <Input
                          value={sec.title || ''}
                          onChange={(e) => setSection(i, { title: e.target.value })}
                          placeholder={info.label}
                          className="bg-white/5 border-white/10 text-white h-9 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Descripción / Subtítulo</label>
                        <Input
                          value={sec.subtitle || ''}
                          onChange={(e) => setSection(i, { subtitle: e.target.value })}
                          placeholder="Texto secundario debajo del título..."
                          className="bg-white/5 border-white/10 text-white h-9 text-sm"
                        />
                      </div>
                      {isCustom && (
                        <div>
                          <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Categoría fuente</label>
                          <select
                            value={sec.category || ''}
                            onChange={(e) => setSection(i, { category: e.target.value })}
                            className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2 focus:outline-none focus:border-orange-500/40"
                          >
                            <option value="" className="bg-neutral-950">— Elige categoría —</option>
                            {allCategories.map((c) => (
                              <option key={c.slug} value={c.name} className="bg-neutral-950">{c.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {(!['hero', 'categories', 'benefits', 'testimonials', 'how_to_order'].includes(sec.id) || isCustom) && (
                        <div>
                          <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Productos a mostrar</label>
                          <select
                            value={sec.limit || 10}
                            onChange={(e) => setSection(i, { limit: Number(e.target.value) })}
                            className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2 focus:outline-none focus:border-orange-500/40"
                          >
                            {Array.from({ length: 12 }).map((_, n) => (
                              <option key={n + 1} value={n + 1} className="bg-neutral-950">{n + 1} prod.</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {sec.id === 'benefits' && (
                        <div className="md:col-span-3 mt-3 pt-3 border-t border-white/5 space-y-3" data-testid="benefits-editor">
                          <p className="text-xs font-bold text-orange-400">Configuración de las 3 tarjetas de beneficios:</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {Array.from({ length: 3 }).map((_, cardIdx) => {
                              const cards = sec.cards || [
                                { icon: 'flame', title: 'Vapes Desechables', desc: 'Sabor listo para usar', link: '/catalogo?cat=Vape%20Desechable' },
                                { icon: 'zap', title: 'Kits y Mods', desc: 'Máxima potencia', link: '/catalogo?cat=Kits' },
                                { icon: 'sparkles', title: 'E-Juice Premium', desc: 'Sabores originales', link: '/catalogo?cat=e-juice' }
                              ];
                              const card = cards[cardIdx] || { icon: 'flame', title: '', desc: '', link: '' };
                              return (
                                <div key={cardIdx} className="bg-white/[0.02] border border-white/5 rounded-xl p-3 space-y-2">
                                  <div className="flex items-center gap-1.5 border-b border-white/5 pb-1.5 mb-1.5">
                                    <span className="text-[10px] bg-orange-500/10 text-orange-400 px-1.5 py-0.5 rounded font-bold">Tarjeta {cardIdx + 1}</span>
                                  </div>
                                  <div>
                                    <label className="text-[9px] uppercase tracking-wider font-bold text-white/40 mb-1 block">Título</label>
                                    <Input
                                      value={card.title || ''}
                                      onChange={(e) => setBenefitCard(i, cardIdx, { title: e.target.value })}
                                      placeholder="Ej. Envío Express"
                                      className="bg-white/5 border-white/10 text-white h-8 text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[9px] uppercase tracking-wider font-bold text-white/40 mb-1 block">Descripción</label>
                                    <Input
                                      value={card.desc || ''}
                                      onChange={(e) => setBenefitCard(i, cardIdx, { desc: e.target.value })}
                                      placeholder="Ej. En menos de 2 horas"
                                      className="bg-white/5 border-white/10 text-white h-8 text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[9px] uppercase tracking-wider font-bold text-white/40 mb-1 block">Enlace (Vínculo)</label>
                                    <Input
                                      value={card.link || ''}
                                      onChange={(e) => setBenefitCard(i, cardIdx, { link: e.target.value })}
                                      placeholder="Ej. /catalogo o URL externa"
                                      className="bg-white/5 border-white/10 text-white h-8 text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[9px] uppercase tracking-wider font-bold text-white/40 mb-1 block">Icono</label>
                                    <select
                                      value={card.icon || 'flame'}
                                      onChange={(e) => setBenefitCard(i, cardIdx, { icon: e.target.value })}
                                      className="w-full h-8 rounded-md bg-neutral-900 border border-white/10 text-white text-xs px-2 focus:outline-none focus:border-orange-500"
                                    >
                                      <option value="flame">🔥 Fuego</option>
                                      <option value="zap">⚡ Rayo</option>
                                      <option value="sparkles">✨ Chispas</option>
                                      <option value="truck">🚚 Camión</option>
                                      <option value="shield">🛡️ Escudo</option>
                                      <option value="award">🏆 Medalla</option>
                                      <option value="credit-card">💳 Tarjeta</option>
                                    </select>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Popular searches ── */}
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Search className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Búsquedas populares</h3>
          </div>
          <p className="text-white/50 text-xs mb-4">Etiquetas que aparecen en el buscador del sitio. Agrégalas, edítalas o elimínalas.</p>

          <div className="space-y-2 mb-3">
            {(s.popular_searches && s.popular_searches.length
              ? s.popular_searches
              : ['Maskking', 'Ay Papa', 'Fasta', 'e-juice', 'Pods', 'Vape desechable', 'Kits', 'Resistencias']
            ).map((term, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={term}
                  onChange={(e) => {
                    const arr = [...(s.popular_searches || ['Maskking', 'Ay Papa', 'Fasta', 'e-juice', 'Pods', 'Vape desechable', 'Kits', 'Resistencias'])];
                    arr[i] = e.target.value;
                    setS({ ...s, popular_searches: arr });
                  }}
                  placeholder="Ej: Maskking"
                  className="bg-white/5 border-white/10 text-white flex-1"
                  data-testid={`admin-popular-search-${i}`}
                />
                <button
                  type="button"
                  onClick={() => {
                    const arr = [...(s.popular_searches || ['Maskking', 'Ay Papa', 'Fasta', 'e-juice', 'Pods', 'Vape desechable', 'Kits', 'Resistencias'])];
                    arr.splice(i, 1);
                    setS({ ...s, popular_searches: arr });
                  }}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-white/40 flex items-center justify-center transition-colors"
                  data-testid={`admin-popular-search-del-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setS({ ...s, popular_searches: [...(s.popular_searches || []), ''] })}
            className="flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300 font-semibold transition-colors"
            data-testid="admin-popular-search-add"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar búsqueda
          </button>

          {(s.popular_searches || []).filter(Boolean).length > 0 && (
            <div className="mt-4 rounded-xl border border-white/10 bg-black p-3">
              <p className="text-[10px] uppercase tracking-widest text-white/30 font-bold mb-2">Vista previa</p>
              <div className="flex flex-wrap gap-2">
                {(s.popular_searches || []).filter(Boolean).map((t) => (
                  <span key={t} className="h-9 px-4 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-sm font-semibold flex items-center">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ── Proceso de Compra ── */}
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Proceso de Compra</h3>
          </div>
          <p className="text-white/50 text-xs mb-5">Edita títulos, descripciones e íconos de cada paso en la sección "¿Cómo hago un pedido?" de la home.</p>
          <div className="space-y-4">
            {([
              { title: 'Realiza tu Pedido', desc: 'Explora el catálogo, elige tus productos y agrégalos al carrito. Selecciona tu método de pago y confirma tu orden.', icon: 'shopping-cart' },
              { title: 'Pago', desc: 'Paga en efectivo al momento de la entrega o vía transferencia bancaria. Si pagas por transferencia, sube el screenshot de tu comprobante antes de confirmar el pedido.', icon: 'credit-card' },
              { title: 'Confirmación', desc: 'Una vez confirmado tu pago, recibirás un correo y una notificación en la app con el estado de tu pedido: En proceso, En camino y Entregado.', icon: 'bell' },
              { title: 'Recibe tu Pedido', desc: 'Entregamos el mismo día. El tiempo estimado es de 20 minutos a 3 horas según tu ubicación dentro de nuestra zona de cobertura.', icon: 'truck' },
            ].map((def, idx) => {
              const rawSteps = s.how_to_order_steps;
              const steps = Array.isArray(rawSteps) && rawSteps.length >= 4
                ? rawSteps
                : [
                    { title: 'Realiza tu Pedido', desc: 'Explora el catálogo, elige tus productos y agrégalos al carrito. Selecciona tu método de pago y confirma tu orden.', icon: 'shopping-cart' },
                    { title: 'Pago', desc: 'Paga en efectivo al momento de la entrega o vía transferencia bancaria. Si pagas por transferencia, sube el screenshot de tu comprobante antes de confirmar el pedido.', icon: 'credit-card' },
                    { title: 'Confirmación', desc: 'Una vez confirmado tu pago, recibirás un correo y una notificación en la app con el estado de tu pedido: En proceso, En camino y Entregado.', icon: 'bell' },
                    { title: 'Recibe tu Pedido', desc: 'Entregamos el mismo día. El tiempo estimado es de 20 minutos a 3 horas según tu ubicación dentro de nuestra zona de cobertura.', icon: 'truck' },
                  ];
              const step = steps[idx] || def;
              const update = (patch) => {
                const next = [...steps];
                next[idx] = { ...step, ...patch };
                setS({ ...s, how_to_order_steps: next });
              };
              const ICON_OPTIONS = [
                { name: 'shopping-cart', Icon: ShoppingCart, label: 'Carrito' },
                { name: 'credit-card', Icon: CreditCard, label: 'Pago' },
                { name: 'bell', Icon: Bell, label: 'Campana' },
                { name: 'truck', Icon: Truck, label: 'Camión' },
                { name: 'package', Icon: Package, label: 'Paquete' },
                { name: 'check-circle', Icon: CheckCircle, label: 'Check' },
                { name: 'zap', Icon: Zap, label: 'Rayo' },
                { name: 'shield', Icon: Shield, label: 'Escudo' },
                { name: 'award', Icon: Award, label: 'Medalla' },
                { name: 'clock', Icon: Clock, label: 'Reloj' },
                { name: 'map-pin', Icon: MapPin, label: 'Mapa' },
                { name: 'gift', Icon: Gift, label: 'Regalo' },
                { name: 'star', Icon: Star, label: 'Estrella' },
                { name: 'flame', Icon: Flame, label: 'Fuego' },
              ];
              return (
                <div key={idx} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-7 h-7 rounded-full bg-orange-500/15 text-orange-400 text-xs font-black flex items-center justify-center shrink-0">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="text-xs text-white/50 font-semibold uppercase tracking-wider">Paso {idx + 1}</span>
                  </div>
                  <p className="text-xs text-white/40 mb-2 uppercase tracking-wider">Ícono</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {ICON_OPTIONS.map(({ name, Icon, label }) => (
                      <button
                        key={name}
                        type="button"
                        title={label}
                        onClick={() => update({ icon: name })}
                        className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all duration-150 ${
                          (step.icon || def.icon) === name
                            ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                            : 'border-white/10 bg-white/5 text-white/40 hover:border-orange-500/40 hover:text-orange-400'
                        }`}
                        data-testid={`admin-how-to-order-icon-${idx}-${name}`}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={step.title}
                      onChange={(e) => update({ title: e.target.value })}
                      placeholder={def.title}
                      className="bg-white/5 border-white/10 text-white text-sm"
                      data-testid={`admin-how-to-order-title-${idx}`}
                    />
                    <textarea
                      value={step.desc}
                      onChange={(e) => update({ desc: e.target.value })}
                      placeholder={def.desc}
                      rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded-md text-white text-xs px-3 py-2 resize-none outline-none focus:border-orange-500/50"
                      data-testid={`admin-how-to-order-desc-${idx}`}
                    />
                  </div>
                </div>
              );
            }))}
          </div>
        </section>

        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5" data-testid="home-subcategories-section">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <HomeIcon className="w-4 h-4 text-orange-400" />
                <h3 className="text-white font-bold">Sub-categorías de "Los más vendidos"</h3>
              </div>
              <p className="text-white/50 text-xs mt-1">Cada sub-categoría muestra un grid con los 4 productos más vendidos de la categoría fuente. Elige nombre visible, color de acento y categoría de la tienda.</p>
            </div>
            <Button onClick={addSubcat} size="sm" className="bg-orange-500 hover:bg-orange-600 text-black font-bold shrink-0" data-testid="add-home-subcat">
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>
          <div className="space-y-2">
            {(s.home_subcategories || []).length === 0 ? (
              <p className="text-white/40 text-xs italic">Sin sub-categorías. Añade una para activar el bloque en el home.</p>
            ) : (s.home_subcategories || []).map((sub, i) => {
              const total = (s.home_subcategories || []).length;
              const hasCat = allCategories.some((c) => c.name === sub.category);
              return (
                <div key={sub.id || i} data-testid={`home-subcat-row-${i}`} className={`rounded-xl border p-3 transition ${sub.visible === false ? 'border-white/5 bg-white/[0.02] opacity-60' : 'border-white/10 bg-black/30'}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px] font-black flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate flex items-center gap-2">
                        <span className="inline-block w-2 h-4 rounded-sm" style={{ backgroundColor: sub.color || '#f97316' }}></span>
                        {sub.label || 'Sin nombre'}
                      </p>
                      <p className="text-white/40 text-[10px] uppercase tracking-wider">Categoría fuente: {sub.category || '—'}{!hasCat && sub.category ? ' (no existe)' : ''}</p>
                    </div>
                    <button
                      onClick={() => setSubcat(i, { visible: sub.visible === false })}
                      title={sub.visible === false ? 'Mostrar' : 'Ocultar'}
                      className={`w-9 h-9 rounded-md inline-flex items-center justify-center transition ${sub.visible === false ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20' : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'}`}
                    >
                      {sub.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => moveSubcat(i, -1)} disabled={i === 0} className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center disabled:opacity-30" title="Subir"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => moveSubcat(i, 1)} disabled={i === total - 1} className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center disabled:opacity-30" title="Bajar"><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => removeSubcat(i)} className="w-9 h-9 rounded-md bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 inline-flex items-center justify-center" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                  </div>

                  {sub.visible !== false && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 pl-8">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Nombre visible</label>
                        <Input
                          value={sub.label || ''}
                          onChange={(e) => setSubcat(i, { label: e.target.value })}
                          placeholder="Ej. VHILL 32K"
                          className="bg-white/5 border-white/10 text-white h-9 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Categoría fuente</label>
                        <select
                          value={sub.category || ''}
                          onChange={(e) => setSubcat(i, { category: e.target.value })}
                          className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2 focus:outline-none focus:border-orange-500/40"
                        >
                          <option value="" className="bg-neutral-950">— Elige categoría —</option>
                          {allCategories.map((c) => (
                            <option key={c.slug} value={c.name} className="bg-neutral-950">{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Productos a mostrar</label>
                        <select
                          value={sub.limit || 4}
                          onChange={(e) => setSubcat(i, { limit: Number(e.target.value) })}
                          className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2 focus:outline-none focus:border-orange-500/40"
                        >
                          {Array.from({ length: 12 }).map((_, n) => (
                            <option key={n + 1} value={n + 1} className="bg-neutral-950">{n + 1} prod.</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Color de acento</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={sub.color || '#f97316'}
                            onChange={(e) => setSubcat(i, { color: e.target.value })}
                            className="w-9 h-9 rounded-md border border-white/10 bg-transparent cursor-pointer"
                          />
                          <Input
                            value={sub.color || '#f97316'}
                            onChange={(e) => setSubcat(i, { color: e.target.value })}
                            className="bg-white/5 border-white/10 text-white h-9 text-sm font-mono"
                            placeholder="#f97316"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-white font-bold">Barra de anuncios (marquee)</h3>
              <p className="text-white/50 text-xs mt-1">Textos que se muestran en la barra naranja del header. Se recomienda usar emojis para destacar.</p>
            </div>
          </div>
          <div className="space-y-2">
            {(s.marquee_messages || []).map((m, i) => {
              const total = (s.marquee_messages || []).length;
              return (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px] font-black flex items-center justify-center shrink-0">{i + 1}</span>
                  <Input
                    value={m}
                    onChange={(e) => setMsg(i, e.target.value)}
                    placeholder="🚚 Envío gratis en compras de $1,500"
                    className="bg-white/5 border-white/10 text-white flex-1"
                  />
                  <EmojiPicker onPick={(e) => setMsg(i, `${e} ${(m || '').trimStart()}`)} />
                  <button
                    onClick={() => moveMsg(i, -1)}
                    disabled={i === 0}
                    data-testid={`marquee-up-${i}`}
                    className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Subir"
                  ><ArrowUp className="w-4 h-4" /></button>
                  <button
                    onClick={() => moveMsg(i, 1)}
                    disabled={i === total - 1}
                    data-testid={`marquee-down-${i}`}
                    className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Bajar"
                  ><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => rmMsg(i)} className="w-9 h-9 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 inline-flex items-center justify-center" title="Eliminar"><X className="w-4 h-4" /></button>
                </div>
              );
            })}
            <Button onClick={addMsg} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10">
              <Plus className="w-4 h-4 mr-1" /> Agregar mensaje
            </Button>
          </div>

          {/* Velocidad del marquee */}
          <div className="mt-5 pt-5 border-t border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Gauge className="w-4 h-4 text-orange-400" />
              <p className="text-white font-bold text-sm">Velocidad del scroll</p>
              <span className="ml-auto text-[11px] text-white/50 font-semibold">
                {s.marquee_speed || 40}s por ciclo · {s.marquee_speed <= 15 ? 'Muy rápido' : s.marquee_speed <= 30 ? 'Rápido' : s.marquee_speed <= 50 ? 'Medio' : 'Lento'}
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {SPEED_PRESETS.map((p) => {
                const active = Number(s.marquee_speed) === p.value;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setS({ ...s, marquee_speed: p.value })}
                    data-testid={`speed-${p.id}-btn`}
                    className={`px-3 h-8 rounded-full text-[11px] font-black uppercase tracking-wider transition-colors border ${
                      active
                        ? 'bg-orange-500 text-black border-orange-500'
                        : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            <input
              type="range"
              min={8}
              max={120}
              step={1}
              value={s.marquee_speed || 40}
              onChange={(e) => setS({ ...s, marquee_speed: Number(e.target.value) })}
              data-testid="speed-slider"
              className="w-full accent-orange-500"
            />
            <div className="flex justify-between text-[10px] text-white/40 uppercase tracking-wider font-bold mt-1">
              <span>Turbo (8s)</span>
              <span>Lento (120s)</span>
            </div>
          </div>
          {(s.marquee_messages || []).length > 0 && (
            <div className="mt-4 rounded-lg overflow-hidden border border-white/10">
              <div className="text-[10px] uppercase tracking-wider font-bold text-white/50 bg-black/40 px-3 py-1.5">Vista previa en vivo</div>
              <div className="bg-orange-500 text-black overflow-hidden">
                <div
                  className="marquee flex whitespace-nowrap py-1.5 text-xs md:text-sm font-bold"
                  style={{ animationDuration: `${s.marquee_speed || 40}s` }}
                >
                  {Array.from({ length: 2 }).map((_, k) => (
                    <span key={k} className="inline-flex items-center gap-3 px-4 shrink-0">
                      {(s.marquee_messages || []).filter((x) => x.trim()).map((x, i) => (
                        <React.Fragment key={i}>
                          <span>{x}</span>
                          <span>•</span>
                        </React.Fragment>
                      ))}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── Popular searches ── */}
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Boxes className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Inventario</h3>
          </div>
          <p className="text-white/50 text-xs mb-3">Controla las alertas de stock y el ajuste rápido en el dashboard.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60">Umbral stock bajo (unidades)</label>
              <Input
                type="number"
                min={1}
                value={s.low_stock_threshold ?? 5}
                onChange={(e) => setS({ ...s, low_stock_threshold: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="low-stock-threshold-input"
              />
              <p className="text-[10px] text-white/40 mt-1">Se avisan productos con menos de este número.</p>
            </div>
            <div>
              <label className="text-xs text-white/60">Ajuste rápido (+ botón)</label>
              <Input
                type="number"
                min={1}
                value={s.restock_step ?? 10}
                onChange={(e) => setS({ ...s, restock_step: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="restock-step-input"
              />
              <p className="text-[10px] text-white/40 mt-1">Unidades que suma el botón &quot;+N&quot; en el dashboard.</p>
            </div>
          </div>
          {/* Preset chips */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold mr-1 self-center">Presets:</span>
            {[5, 10, 20, 50].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setS({ ...s, restock_step: n })}
                className={`px-2.5 h-7 rounded-full text-[10px] font-black uppercase tracking-wider border transition-colors ${
                  Number(s.restock_step) === n
                    ? 'bg-orange-500 text-black border-orange-500'
                    : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                }`}
              >
                +{n}
              </button>
            ))}
          </div>
        </section>

        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-bold mb-3">Envíos</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60">Umbral envío gratis (MXN)</label>
              <Input type="number" value={s.free_shipping_threshold} onChange={(e) => setS({ ...s, free_shipping_threshold: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-white/60">Costo de envío (MXN)</label>
              <Input type="number" value={s.shipping_cost} onChange={(e) => setS({ ...s, shipping_cost: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>
        </section>

        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Palette className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Identidad de marca</h3>
          </div>
          <p className="text-white/50 text-xs mb-4">Configura el logo, nombre y favicon que aparecen en el sitio.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <ImageUploader
                value={s.logo_url || ''}
                onChange={(v) => setS({ ...s, logo_url: v })}
                label="Logo del header"
                hint="PNG recomendado con fondo transparente"
                maxWidth={800}
                aspect="wide"
                format="auto"
                testId="logo-uploader"
              />
              {/* Height / size slider */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-white/60">Alto del logo</label>
                  <span className="text-[10px] text-orange-300 font-black">{s.logo_height ?? 40} px</span>
                </div>
                <input
                  type="range"
                  min={20}
                  max={96}
                  step={1}
                  value={s.logo_height ?? 40}
                  onChange={(e) => setS({ ...s, logo_height: Number(e.target.value) })}
                  data-testid="logo-height-slider"
                  className="w-full accent-orange-500"
                />
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {[24, 32, 40, 56, 72].map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setS({ ...s, logo_height: h })}
                      className={`px-2 h-6 rounded-full text-[10px] font-black uppercase tracking-wider border transition-colors ${
                        Number(s.logo_height) === h
                          ? 'bg-orange-500 text-black border-orange-500'
                          : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {h}px
                    </button>
                  ))}
                </div>
                {/* Preview matching header */}
                {s.logo_url && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-white/10">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-white/50 bg-black/40 px-3 py-1.5">Vista previa en el header</div>
                    <div className="bg-black px-4 py-3 flex items-center gap-3">
                      <img
                        src={s.logo_url}
                        alt="preview"
                        style={{ height: `${Math.min(96, Number(s.logo_height) || 40)}px` }}
                        className="w-auto object-contain"
                      />
                      <span className="text-white/30 text-[10px] uppercase tracking-wider ml-auto">Fondo header</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <ImageUploader
              value={s.favicon_url || ''}
              onChange={(v) => setS({ ...s, favicon_url: v })}
              label="Favicon"
              hint="Ideal: 512×512 PNG cuadrado"
              maxWidth={512}
              quality={0.92}
              format="png"
              testId="favicon-uploader"
            />
          </div>

          <div className="mt-4">
            <label className="text-[11px] uppercase tracking-wider font-bold text-white/60 mb-1.5 block">Nombre del sitio</label>
            <Input
              value={s.site_name || ''}
              onChange={(e) => setS({ ...s, site_name: e.target.value })}
              placeholder="NubesMTY"
              data-testid="site-name-input"
              className="bg-white/5 border-white/10 text-white"
            />
            <p className="text-[10px] text-white/40 mt-1">Aparece en el header si no hay logo cargado.</p>
          </div>
        </section>

        {/* SEO */}
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Search className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">SEO y meta datos</h3>
          </div>
          <p className="text-white/50 text-xs mb-4">Título y descripción que ven Google y las redes sociales al compartir tu sitio.</p>

          <div className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-white/60 mb-1.5 block">Título (meta title)</label>
              <Input
                value={s.meta_title || ''}
                onChange={(e) => setS({ ...s, meta_title: e.target.value })}
                placeholder="NubesMTY · Vapes originales en Monterrey"
                maxLength={60}
                data-testid="meta-title-input"
                className="bg-white/5 border-white/10 text-white"
              />
              <p className="text-right text-[10px] text-white/40 mt-1">{(s.meta_title || '').length}/60 recomendado</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-white/60 mb-1.5 block">Meta descripción</label>
              <Textarea
                value={s.meta_description || ''}
                onChange={(e) => setS({ ...s, meta_description: e.target.value })}
                placeholder="La mejor tienda de vapes, pods y e-juice en México..."
                rows={3}
                maxLength={160}
                data-testid="meta-description-input"
                className="bg-white/5 border-white/10 text-white resize-none"
              />
              <p className="text-right text-[10px] text-white/40 mt-1">{(s.meta_description || '').length}/160 recomendado</p>
            </div>

            {/* SERP preview */}
            <div className="rounded-lg border border-white/10 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wider font-bold text-black/50 mb-1">Vista previa Google</p>
              <p className="text-[#1a0dab] text-lg font-medium truncate leading-tight">
                {s.meta_title || s.site_name || 'Título del sitio'}
              </p>
              <p className="text-[#006621] text-xs mt-0.5">{typeof window !== 'undefined' ? window.location.hostname : 'vapeandoando.mx'}</p>
              <p className="text-[#4d5156] text-sm mt-1 line-clamp-2">
                {s.meta_description || 'Descripción del sitio para SEO...'}
              </p>
            </div>
          </div>
        </section>

        {/* Datos bancarios (Transferencia) */}
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5" data-testid="admin-bank-details-section">
          <div className="flex items-center gap-2 mb-1">
            <Landmark className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Datos bancarios (Transferencia)</h3>
          </div>
          <p className="text-xs text-white/50 mb-4">Aparecerán en el checkout cuando el cliente elija <span className="text-orange-300 font-semibold">Transferencia</span>. Deja en blanco los campos que no uses; se ocultarán del cliente.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60">Banco</label>
              <Input
                value={s.bank_name || ''}
                onChange={(e) => setS({ ...s, bank_name: e.target.value })}
                placeholder="BBVA"
                className="bg-white/5 border-white/10 text-white"
                data-testid="admin-bank-name"
              />
            </div>
            <div>
              <label className="text-xs text-white/60">Titular de la cuenta</label>
              <Input
                value={s.bank_account_holder || ''}
                onChange={(e) => setS({ ...s, bank_account_holder: e.target.value })}
                placeholder="NubesMTY"
                className="bg-white/5 border-white/10 text-white"
                data-testid="admin-bank-holder"
              />
            </div>
            <div>
              <label className="text-xs text-white/60">CLABE interbancaria (18 dígitos)</label>
              <Input
                value={s.bank_clabe || ''}
                onChange={(e) => setS({ ...s, bank_clabe: e.target.value })}
                placeholder="012 000 00000000000 0"
                className="bg-white/5 border-white/10 text-white font-mono"
                data-testid="admin-bank-clabe"
              />
            </div>
            <div>
              <label className="text-xs text-white/60">Número de cuenta (opcional)</label>
              <Input
                value={s.bank_account_number || ''}
                onChange={(e) => setS({ ...s, bank_account_number: e.target.value })}
                placeholder="1234 5678 9012 3456"
                className="bg-white/5 border-white/10 text-white font-mono"
                data-testid="admin-bank-account"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-white/60">Nota / referencia (opcional)</label>
              <Input
                value={s.bank_reference_note || ''}
                onChange={(e) => setS({ ...s, bank_reference_note: e.target.value })}
                placeholder="Coloca tu nombre como referencia en la transferencia"
                className="bg-white/5 border-white/10 text-white"
                data-testid="admin-bank-note"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider font-bold text-white/40 mb-2">Vista previa en checkout</p>
            <div className="rounded-lg bg-black/40 border border-white/10 p-3 text-xs space-y-1.5 max-w-md" data-testid="admin-bank-preview">
              {s.bank_name ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white/45 uppercase tracking-wider text-[10px] font-bold">Banco</span>
                  <span className="text-white font-bold">{s.bank_name}</span>
                </div>
              ) : null}
              {s.bank_account_holder ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white/45 uppercase tracking-wider text-[10px] font-bold">Titular</span>
                  <span className="text-white font-bold">{s.bank_account_holder}</span>
                </div>
              ) : null}
              {s.bank_clabe ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white/45 uppercase tracking-wider text-[10px] font-bold">CLABE</span>
                  <span className="text-orange-300 font-mono font-bold">{s.bank_clabe}</span>
                </div>
              ) : null}
              {s.bank_account_number ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white/45 uppercase tracking-wider text-[10px] font-bold">Cuenta</span>
                  <span className="text-white font-mono font-bold">{s.bank_account_number}</span>
                </div>
              ) : null}
              {s.bank_reference_note ? (
                <p className="pt-1.5 border-t border-white/10 text-white/60 leading-snug">{s.bank_reference_note}</p>
              ) : null}
              {!s.bank_name && !s.bank_account_holder && !s.bank_clabe && !s.bank_account_number && !s.bank_reference_note ? (
                <p className="text-white/40 italic text-center py-2">Completa al menos un campo para que aparezca en el checkout.</p>
              ) : null}
            </div>
          </div>
        </section>

        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Type className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Contacto y footer</h3>
          </div>
          <p className="text-xs text-white/50 mb-4">Datos que aparecen en la parte inferior de todas las páginas.</p>

          <div className="space-y-4">
            {/* Contact info */}
            <div>
              <p className="text-[11px] font-bold tracking-wider uppercase text-white/40 mb-2">Datos de contacto</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60">Correo de contacto</label>
                  <Input value={s.contact_email || ''} onChange={(e) => setS({ ...s, contact_email: e.target.value })} placeholder="contacto@nubesmty.com" className="bg-white/5 border-white/10 text-white" data-testid="admin-contact-email" />
                </div>
                <div>
                  <label className="text-xs text-white/60">Teléfono de contacto</label>
                  <Input value={s.contact_phone || ''} onChange={(e) => setS({ ...s, contact_phone: e.target.value })} placeholder="+52 81 1234 5678" className="bg-white/5 border-white/10 text-white" data-testid="admin-contact-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-white/60 flex items-center gap-1"><MessageCircle className="w-3 h-3 text-[#25D366]" /> WhatsApp (formato internacional, para el botón wa.me)</label>
                  <Input value={s.whatsapp_number || ''} onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })} placeholder="+5218139147429" className="bg-white/5 border-white/10 text-white" data-testid="admin-whatsapp-number" />
                </div>
              </div>
            </div>

            {/* Social URLs */}
            <div>
              <p className="text-[11px] font-bold tracking-wider uppercase text-white/40 mb-2">Redes sociales (Síguenos)</p>
              <p className="text-[11px] text-white/50 mb-3">Deja en blanco las que no uses; solo se mostrarán las que tengan URL.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: 'instagram_url', label: 'Instagram', Icon: Instagram, placeholder: 'https://instagram.com/nubesmty' },
                  { key: 'facebook_url', label: 'Facebook', Icon: Facebook, placeholder: 'https://facebook.com/nubesmty' },
                  { key: 'youtube_url', label: 'YouTube', Icon: Youtube, placeholder: 'https://youtube.com/@nubesmty' },
                  { key: 'twitter_url', label: 'X / Twitter', Icon: Twitter, placeholder: 'https://x.com/nubesmty' },
                  { key: 'tiktok_url', label: 'TikTok', Icon: Music2, placeholder: 'https://tiktok.com/@nubesmty' },
                ].map((sc) => {
                  const Ic = sc.Icon;
                  return (
                    <div key={sc.key}>
                      <label className="text-xs text-white/60 flex items-center gap-1.5"><Ic className="w-3.5 h-3.5 text-orange-400" /> {sc.label}</label>
                      <Input
                        value={s[sc.key] || ''}
                        onChange={(e) => setS({ ...s, [sc.key]: e.target.value })}
                        placeholder={sc.placeholder}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid={`admin-social-${sc.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Copy blocks */}
            <div>
              <p className="text-[11px] font-bold tracking-wider uppercase text-white/40 mb-2">Textos del footer</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-white/60">Nota del footer (bajo el logo)</label>
                  <Textarea value={s.footer_note || ''} onChange={(e) => setS({ ...s, footer_note: e.target.value })} rows={2} placeholder="La mejor tienda de vapes en México." className="bg-white/5 border-white/10 text-white" data-testid="admin-footer-note" />
                </div>
                <div>
                  <label className="text-xs text-white/60">Disclaimer legal (bajo redes sociales)</label>
                  <Textarea value={s.footer_disclaimer || ''} onChange={(e) => setS({ ...s, footer_disclaimer: e.target.value })} rows={2} placeholder="El vapeo está destinado exclusivamente para adultos consumidores de nicotina..." className="bg-white/5 border-white/10 text-white" data-testid="admin-footer-disclaimer" />
                </div>
                <div>
                  <label className="text-xs text-white/60">Copyright (usa <code className="text-orange-400">{'{year}'}</code> para el año dinámico)</label>
                  <Input value={s.footer_copyright || ''} onChange={(e) => setS({ ...s, footer_copyright: e.target.value })} placeholder="NubesMTY © {year}. Todos los derechos reservados." className="bg-white/5 border-white/10 text-white" data-testid="admin-footer-copyright" />
                </div>
              </div>
            </div>

            {/* Badges editor */}
            <BadgesEditor s={s} setS={setS} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsSection;

/* ─── BadgesEditor ─────────────────────────────────────────────── */

const ICON_OPTIONS = [
  { key: 'truck',        Icon: Truck,          label: 'Envío / Camión' },
  { key: 'shield',       Icon: Shield,         label: 'Garantía / Escudo' },
  { key: 'award',        Icon: Award,          label: 'Original / Trofeo' },
  { key: 'card',         Icon: CreditCard,     label: 'Pago / Tarjeta' },
  { key: 'star',         Icon: Star,           label: 'Estrella' },
  { key: 'zap',          Icon: Zap,            label: 'Rapidez / Rayo' },
  { key: 'package',      Icon: Package,        label: 'Paquete' },
  { key: 'check',        Icon: CheckCircle,    label: 'Verificado / Check' },
  { key: 'lock',         Icon: Lock,           label: 'Seguro / Candado' },
  { key: 'gift',         Icon: Gift,           label: 'Regalo / Oferta' },
  { key: 'phone',        Icon: Phone,          label: 'Soporte / Teléfono' },
  { key: 'clock',        Icon: Clock,          label: 'Entrega rápida' },
  { key: 'returns',      Icon: RotateCcw,      label: 'Devoluciones' },
  { key: 'handshake',    Icon: HeartHandshake, label: 'Confianza' },
];

const ICON_MAP = Object.fromEntries(ICON_OPTIONS.map((o) => [o.key, o.Icon]));

const DEFAULT_BADGES = [
  { i: 'truck',  t: 'Envío gratis',   d: 'En cualquier compra' },
  { i: 'shield', t: 'Garantía',       d: 'Contra defectos de fábrica' },
  { i: 'award',  t: '100% Original',  d: 'Marcas certificadas' },
  { i: 'card',   t: 'Pago seguro',    d: 'Múltiples métodos' },
];

function BadgesEditor({ s, setS }) {
  const badges = (s.footer_badges && s.footer_badges.length) ? s.footer_badges : DEFAULT_BADGES;

  const update = (idx, patch) => {
    const next = badges.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    setS({ ...s, footer_badges: next });
  };

  return (
    <div>
      <p className="text-[11px] font-bold tracking-wider uppercase text-white/40 mb-1">Barras de confianza del footer</p>
      <p className="text-[11px] text-white/50 mb-3">Edita el icono, título y descripción de cada barra. Se guardan con el botón "Guardar cambios".</p>

      {/* Live preview */}
      <div className="rounded-xl border border-white/10 bg-black p-4 mb-4">
        <p className="text-[10px] uppercase tracking-widest text-white/30 font-bold mb-3">Vista previa</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {badges.map((b, i) => {
            const Ic = ICON_MAP[b.i] || Award;
            return (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center shrink-0">
                  <Ic className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-white text-xs truncate">{b.t || 'Título'}</p>
                  <p className="text-[11px] text-white/50 truncate">{b.d || 'Descripción'}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Editor cards */}
      <div className="space-y-3">
        {badges.map((b, i) => {
          const Ic = ICON_MAP[b.i] || Award;
          return (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center shrink-0">
                  <Ic className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-white/60 uppercase tracking-wider">Barra {i + 1}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Icon picker */}
                <div>
                  <label className="text-[11px] text-white/50 mb-1 block">Ícono</label>
                  <div className="grid grid-cols-5 gap-1.5">
                    {ICON_OPTIONS.map(({ key, Icon: Ico, label }) => (
                      <button
                        key={key}
                        type="button"
                        title={label}
                        onClick={() => update(i, { i: key })}
                        className={`w-full aspect-square rounded-lg flex items-center justify-center transition-colors
                          ${b.i === key
                            ? 'bg-orange-500 text-black'
                            : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'}`}
                        data-testid={`admin-badge-icon-${i}-${key}`}
                      >
                        <Ico className="w-3.5 h-3.5" />
                      </button>
                    ))}
                  </div>
                </div>
                {/* Title */}
                <div>
                  <label className="text-[11px] text-white/50 mb-1 block">Título</label>
                  <Input
                    value={b.t}
                    onChange={(e) => update(i, { t: e.target.value })}
                    placeholder="Envío gratis"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid={`admin-badge-title-${i}`}
                  />
                </div>
                {/* Description */}
                <div>
                  <label className="text-[11px] text-white/50 mb-1 block">Descripción</label>
                  <Input
                    value={b.d}
                    onChange={(e) => update(i, { d: e.target.value })}
                    placeholder="En compras +$1,500"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid={`admin-badge-desc-${i}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
